import React, { useEffect, useState } from 'react';
import { ArrowLeft, Bomb, Check, Lock } from 'lucide-react';
import { Button } from './ui/button';

// 4 weapons (matches /app/backend/data/weapons.py catalog)
const WEAPON_META = {
  assault_rifle:  { icon: '🎯', name: 'Assault Rifle',  desc: '100 rounds · neon-green tracers · +80% dmg', color: '#14F195' },
  rocket_launcher:{ icon: '🚀', name: 'Rocket Launcher',desc: '10 rockets · area damage',                 color: '#FF4444' },
  machine_gun:    { icon: '🔫', name: 'Machine Gun',    desc: '30 rounds · twin cannons · +50 armor',     color: '#4A90D9' },
  // Backward-compat alias for legacy purchases
  mini_tank:      { icon: '🛡️', name: 'Mini Tank',      desc: '30 rounds · twin cannons · +50 armor',     color: '#4A90D9' },
};

const LoadoutSelector = ({
  skins = [],
  ownedSkins = [],
  selectedSkin,
  onSelectSkin,
  ownedWeapons = [],
  selectedWeapon,
  onSelectWeapon,
  bringGrenade,
  onToggleGrenade,
  onBack,
  onShop,
}) => {
  // Filter out weapons the player doesn't own
  const purchasableWeapons = ['assault_rifle', 'rocket_launcher', 'machine_gun'];
  // Treat legacy mini_tank as machine_gun in the UI
  const normalizedOwned = new Set(
    ownedWeapons.map(w => (w === 'mini_tank' ? 'machine_gun' : w))
  );
  const ownsAnyWeapon = purchasableWeapons.some(w => normalizedOwned.has(w));
  const ownsGrenade = ownedWeapons.includes('grenade');

  // Default the selection to "none" if the previously-selected weapon is no longer owned
  useEffect(() => {
    if (selectedWeapon && selectedWeapon !== 'none' && !normalizedOwned.has(selectedWeapon)) {
      onSelectWeapon('none');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeapon, ownedWeapons]);

  const skinList = skins.length > 0 ? skins : [{ id: 'classic', name: 'Classic Soldier', color: '#FF3B30' }];

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button onClick={onBack} variant="ghost" className="text-[#A1A1AA] hover:text-white" data-testid="loadout-back-btn">
            <ArrowLeft className="w-5 h-5 mr-2" /> Back
          </Button>
          <h1 className="font-game text-2xl text-[#14F195]">LOADOUT</h1>
          <div className="w-16" />
        </div>

        {/* Skin Picker */}
        <div className="glass-card p-5 mb-6" data-testid="loadout-skin-section">
          <h2 className="font-game text-sm text-[#9945FF] mb-4">CHARACTER SKIN</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {skinList.map(skin => {
              const isOwned = ownedSkins.includes(skin.id) || skin.price === 0;
              const isSelected = selectedSkin?.id === skin.id;
              return (
                <button
                  key={skin.id}
                  disabled={!isOwned}
                  onClick={() => onSelectSkin(skin)}
                  data-testid={`loadout-skin-${skin.id}`}
                  className={`relative bg-[#0a0a0a] rounded-lg p-3 border-2 transition-all ${
                    isSelected ? 'scale-105' : ''
                  } ${isOwned ? 'cursor-pointer hover:scale-105' : 'opacity-50 cursor-not-allowed'}`}
                  style={{
                    borderColor: isSelected ? skin.color : '#222',
                    boxShadow: isSelected ? `0 0 16px ${skin.color}55` : 'none'
                  }}>
                  <div className="w-full aspect-square rounded flex items-center justify-center mb-2"
                    style={{ backgroundColor: `${skin.color}20` }}>
                    <div className="w-8 h-12 rounded" style={{ backgroundColor: skin.color }} />
                  </div>
                  <p className="text-[10px] font-game text-white text-center truncate">{skin.name}</p>
                  {!isOwned && <Lock className="absolute top-1.5 right-1.5 w-3 h-3 text-[#666]" />}
                  {isSelected && (
                    <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: skin.color }}>
                      <Check className="w-3 h-3 text-black" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Weapon Picker */}
        <div className="glass-card p-5 mb-6" data-testid="loadout-weapon-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-game text-sm text-[#FF4444]">PRIMARY WEAPON</h2>
            <p className="text-[10px] text-[#666] font-game">PICK ONE</p>
          </div>

          {!ownsAnyWeapon && (
            <div className="bg-[#0a1a0a] border border-[#14F195]/30 rounded-lg p-3 mb-3 text-center">
              <p className="text-xs text-[#A1A1AA]">
                You don't own any weapons yet.{' '}
                {onShop ? (
                  <button onClick={onShop} className="text-[#14F195] underline hover:text-white" data-testid="loadout-go-to-shop-btn">
                    Visit the shop
                  </button>
                ) : 'Visit the shop'}
                {' '}to buy your first one ($5 each, one-time).
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* "None" option */}
            <button
              onClick={() => onSelectWeapon('none')}
              data-testid="loadout-weapon-none"
              className={`bg-[#0a0a0a] rounded-lg p-4 border-2 text-left transition-all hover:scale-[1.01] ${
                selectedWeapon === 'none' || !selectedWeapon ? 'border-[#A1A1AA] shadow-[0_0_12px_rgba(161,161,170,0.3)]' : 'border-[#222]'
              }`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-[#1a1a1a] flex items-center justify-center text-2xl">🚫</div>
                <div className="flex-1">
                  <p className="font-game text-sm text-white">None (Default Pistol)</p>
                  <p className="text-[10px] text-[#A1A1AA]">Unlimited starter ammo, low damage</p>
                </div>
                {(selectedWeapon === 'none' || !selectedWeapon) && (
                  <Check className="w-5 h-5 text-[#A1A1AA]" />
                )}
              </div>
            </button>

            {/* Owned weapons */}
            {purchasableWeapons.map(wid => {
              const meta = WEAPON_META[wid];
              const owned = normalizedOwned.has(wid);
              const isSelected = selectedWeapon === wid;
              return (
                <button
                  key={wid}
                  onClick={() => owned && onSelectWeapon(wid)}
                  disabled={!owned}
                  data-testid={`loadout-weapon-${wid}`}
                  className={`bg-[#0a0a0a] rounded-lg p-4 border-2 text-left transition-all ${
                    owned ? 'hover:scale-[1.01]' : 'opacity-50 cursor-not-allowed'
                  } ${isSelected ? 'shadow-[0_0_16px_rgba(20,241,149,0.3)]' : 'border-[#222]'}`}
                  style={{
                    borderColor: isSelected ? meta.color : '#222'
                  }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded flex items-center justify-center text-2xl"
                      style={{ backgroundColor: `${meta.color}20`, border: `1px solid ${meta.color}55` }}>
                      {meta.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-game text-sm text-white truncate">{meta.name}</p>
                      <p className="text-[10px] text-[#A1A1AA] truncate">{meta.desc}</p>
                    </div>
                    {!owned && <Lock className="w-4 h-4 text-[#666]" />}
                    {isSelected && <Check className="w-5 h-5" style={{ color: meta.color }} />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Grenade Toggle */}
        <div className="glass-card p-5 mb-6" data-testid="loadout-grenade-section">
          <h2 className="font-game text-sm text-[#FF9500] mb-4">GRENADE PACK</h2>
          {!ownsGrenade ? (
            <div className="text-xs text-[#A1A1AA] py-2 text-center">
              <Lock className="w-4 h-4 inline mr-2 align-text-bottom text-[#666]" />
              Grenade Pack not owned. {onShop && (
                <button onClick={onShop} className="text-[#14F195] underline hover:text-white">
                  Buy it in the shop ($5)
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={() => onToggleGrenade(!bringGrenade)}
              data-testid="loadout-grenade-toggle"
              className={`w-full bg-[#0a0a0a] rounded-lg p-4 border-2 text-left transition-all hover:scale-[1.005] ${
                bringGrenade ? 'border-[#FF9500] shadow-[0_0_16px_rgba(255,149,0,0.3)]' : 'border-[#222]'
              }`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded flex items-center justify-center text-2xl"
                  style={{ backgroundColor: '#FF950020', border: '1px solid #FF950055' }}>
                  <Bomb className="w-5 h-5 text-[#FF9500]" />
                </div>
                <div className="flex-1">
                  <p className="font-game text-sm text-white">Bring 10 Grenades</p>
                  <p className="text-[10px] text-[#A1A1AA]">Press G in-game to throw</p>
                </div>
                <div className={`w-12 h-6 rounded-full p-0.5 transition-colors ${bringGrenade ? 'bg-[#FF9500]' : 'bg-[#333]'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${bringGrenade ? 'translate-x-6' : ''}`} />
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Confirm Button */}
        <Button
          onClick={onBack}
          className="game-btn w-full text-base py-5"
          data-testid="loadout-confirm-btn">
          <Check className="w-5 h-5 mr-2" /> CONFIRM LOADOUT
        </Button>
      </div>
    </div>
  );
};

export default LoadoutSelector;
